import {
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  OutlinedInput,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import { ColumnMovedEvent, ColumnResizedEvent, ColumnVisibleEvent, SortChangedEvent, FilterModel, GridReadyEvent, PaginationChangedEvent, ToolPanelVisibleChangedEvent, ColDef } from 'ag-grid-community';
import {
  CellStyle,
  GridApi,
  ICellRendererParams,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SideBarDef,
  ValueFormatterParams
} from 'ag-grid-enterprise';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  GridType,
  InvestorBalanceTransactionDto,
  PaymentMethod,
  Rights,
  SortArgs,
  TransactionStatus,
  TransactionType,
  useInvestorBalanceTransactionsLazyQuery
} from '../../graphql/types';
import useConfig from '../../hooks/useConfig';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { capitalizeFirstLetter, underscoreToSpace } from '../../utils/strings';
import { BarsOutlined, EditTwoTone, ExportOutlined, FilterOutlined, InteractionOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import useAuth from '../../hooks/useAuth';
import { reportApi } from '../../api/reportApi';
import { McaStatusChip } from '../merchants/McaStatusChip';
import {
  TransactionDefaultStatusCell,
  TransactionDefaultTypeCell,
  TransactionStatusCell
} from '../../components/ag-grid/AgGridTransactionsChipCell';
import AgGridDebitAmountCell from '../../components/ag-grid/AgGridDebitAmountCell';
import AgGridCreditAmountCell from '../../components/ag-grid/AgGridCreditAmountCell';
import AgGridCashBalanceChipCell from '../../components/ag-grid/AgGridCashBalanceChipCell';
import AddCashTransaction from './AddCashTransaction';
import { Icon, supportedPaymentMethodIdsforIcons } from '../../components/icons';
import PaymentMethodsLogo from '../../utils/paymentMethodLogo';
import { useTheme } from '@mui/material/styles';
import { debounce } from 'lodash';
import useAgGridOutsideClick from '../../components/ag-grid/useAgGridOutsideClick';
import { applyGridColumnState } from 'utils/agGridUtils';
import McaListTools from '../merchants/McaListTools';
import CustomTooltip from 'components/CustomTooltip';
import ScrollX from 'components/ScrollX';

export interface InvestorBalanceTransactionsProps {
  investorId: string;
  forLastMonths: number;
  paymentMethods: PaymentMethod[];
}

const LEDGER_TRANSACTION_TYPES = [
  TransactionType.Debit,
  TransactionType.DefaultFee,
  TransactionType.NsfFee,
  TransactionType.Syndication,
  TransactionType.FundingWallet,
  TransactionType.Remittance,
  TransactionType.RemittanceFee,
  TransactionType.CrmAccessFee,
  TransactionType.Expense,
  TransactionType.ServiceFee
];

const formatLedgerTransactionType = (value: TransactionType) => {
  if (!value) return '';
  return value === TransactionType.Debit ? 'Credit' : value === TransactionType.Credit ? 'Debit' : capitalizeFirstLetter(value);
};

const expandLedgerTypes = (types: TransactionType[]): TransactionType[] => {
  const expanded = types.includes(TransactionType.Debit) ? [...types, TransactionType.Collection] : [...types];
  return Array.from(new Set(expanded));
};

const LedgerBalanceFilterContext = createContext<{
  filterState: FilterModel | null;
  setFilterState: (filters: FilterModel | null) => void;
}>({
  filterState: null,
  setFilterState: () => { }
});

export const LedgerBalanceFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filterState, setFilterState] = useState<FilterModel | null>(null);
  return <LedgerBalanceFilterContext.Provider value={{ filterState, setFilterState }}>{children}</LedgerBalanceFilterContext.Provider>;
};
const InvestorBalanceTransactions = ({ investorId, forLastMonths, paymentMethods }: InvestorBalanceTransactionsProps) => {
  const { mode } = useConfig();
  const theme = useTheme();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [transaction, setTransaction] = useState<any | null>(null);
  const [add, setAdd] = useState<boolean>(false);
  const handleAdd = () => {
    setAdd(!add);
    if (transaction && !add) setTransaction(null);
  };
  const { user: currentUser } = useAuth();
  const userId = currentUser?.id;
  const keyName = `${userId}_LEDGER_BALANCE_TRANSACTION_LIST`;
  const useCompactView = localStorage.getItem(keyName + '_USE_COMPACT_VIEW') === 'false' ? null : true;
  const perPage = 50;
  const [rowsPerPage, setRowsPerPage] = useState<number>(parseInt(localStorage.getItem(keyName + '_ROWS_PER_PAGE_STATE') || '') || perPage);
  const canManage = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const [fetchData] = useInvestorBalanceTransactionsLazyQuery({
    fetchPolicy: 'network-only'
  });
  const { filterState, setFilterState } = useContext(LedgerBalanceFilterContext);
  const typeFilter = filterState?.type || '';
  const paymentMethodFilter = filterState?.paymentMethodId || '';
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const gridApiRef = useRef<GridApi>();
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<TransactionType[]>([]);
  const [currentTotalRows, setCurrentTotalRows] = useState<number>(0);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);

  const McaNoStatusChipCellRenderer = ({ node, data }: ICellRendererParams) => {
    if (node.isRowPinned()) {
      return null;
    }
    return (
      <McaStatusChip id={data.mcaId} status={data.mcaStatus} label={data.mcaNumber} navigateToDetails={true} investorId={investorId} useCompactViewStyle={useCompactView ? true : false} />
    );
  };

  const defaultColDef = useMemo(() => {
    return {
      suppressHeaderMenuButton: true,
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      autoHeaderHeight: true,
      cellStyle: {
        display: 'flex',
        alignItems: 'center'
      },
      columnChooserParams: {
        suppressColumnFilter: true,
        contractColumnSelection: true
      },
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        suppressHeaderMenuButton: true,
        sort: 'desc',
        headerName: '#',
        field: 'number',
        cellClass: 'ag-centered-cell',
        headerClass: 'ag-header-cell-limegreen-center',
        toolPanelClass: ['ag-header-cell-limegreen-center'],
      },
      {
        resizable: true,
        headerName: 'MCA#',
        field: 'mcaNumber',
        headerClass: 'ag-header-cell-limegreen-center',
        toolPanelClass: ['ag-header-cell-limegreen-center'],
        sortable: true,
        cellClass: 'ag-centered-cell',
        cellRenderer: McaNoStatusChipCellRenderer,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false }
      },
      {
        headerName: 'Date',
        field: 'effectiveDate',
        cellStyle: { direction: 'rtl' } as CellStyle,
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['text-center-lightskyblue'],
        valueGetter: (params) => getFormattedDateUTC(params.data?.effectiveDate)
      },
      {
        headerName: 'Description',
        field: 'notes',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['text-center-lightskyblue'],
      },
      {
        headerName: 'Type',
        initialHide: true,
        field: 'type',
        filter: true,
        cellClass: 'ag-centered-cell',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellRenderer: TransactionDefaultTypeCell,
        cellRendererParams: {
          ledgerTransaction: true,
          useCompactViewStyle: useCompactView ? true : false
        },
        valueFormatter: (params) => (params.value ? capitalizeFirstLetter(params.value) : ''),
        filterParams: {
          values: LEDGER_TRANSACTION_TYPES,
          valueFormatter: ({ value }: ValueFormatterParams) => formatLedgerTransactionType(value)
        }
      },
      {
        headerName: 'Pmt Method',
        field: 'paymentMethodId',
        sortable: false,
        filter: true,
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        headerComponent: () => (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CustomTooltip title="Pmt Method">
              <InteractionOutlined style={{ fontSize: 20 }} />
            </CustomTooltip>
          </Box>
        ),
        cellClass: 'ag-centered-cell',
        tooltipValueGetter: (params) => {
          return params.value ? underscoreToSpace(params.value) : 'UNKNOWN';
        },
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          const paymentMethod = paymentMethods.find((paymentMethod) => paymentMethod.id === data?.paymentMethodId);
          const tooltipTitle = paymentMethod?.id ? underscoreToSpace(paymentMethod.id) : 'UNKNOWN';
          if (supportedPaymentMethodIdsforIcons.includes(paymentMethod?.id)) {
            return (
                <span className="agcell-align">
                <Icon id={paymentMethod?.id} size="xxs" />
              </span>
            );
          } else {
            return <PaymentMethodsLogo paymentMethod={paymentMethod} size={'xs'} mt={useCompactView ? 0 : undefined} />;
          }
        },
        filterParams: {
          values: paymentMethods?.map((paymentMethod) => paymentMethod.id),
          valueFormatter: ({ value }: ValueFormatterParams) => {
            return capitalizeFirstLetter(value);
          }
        }
      },
      {
        headerName: 'Status',
        field: 'status',
        initialHide: true,
        filter: false,
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellClass: 'ag-centered-cell',
        cellRenderer: TransactionStatusCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        filterParams: {
          values: [
            TransactionStatus.Cleared,
            TransactionStatus.Nsf,
            TransactionStatus.Returned,
            TransactionStatus.Cancelled,
            TransactionStatus.Pending,
            TransactionStatus.Processed
          ]
        }
      },
      {
        headerName: 'Credit',
        field: 'debit',
        cellRenderer: AgGridDebitAmountCell,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellStyle: { direction: 'rtl' } as CellStyle,
      },
      {
        headerName: 'Debit',
        field: 'credit',
        cellRenderer: AgGridCreditAmountCell,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellStyle: { direction: 'rtl' } as CellStyle,
      },
      {
        headerName: 'Balance',
        field: 'runningBalance',
        cellRenderer: AgGridCashBalanceChipCell,
        cellRendererParams: {
          disableChipStyles: true
        },
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        cellClass: 'ag-pin-column-' + mode,
        cellStyle: { direction: 'rtl' } as CellStyle,
      },
      {
        field: 'actions',
        headerName: '',
        hide: !canManage,
        initialHide: !canManage,
        headerComponent: () => {
          return (
            <Tooltip
              title="Actions"
              PopperProps={{
                sx: {
                  '& .MuiTooltip-tooltip': {
                    bgcolor: '#000000',
                    color: '#FFFFFF'
                  }
                }
              }}
            >
              <BarsOutlined style={{ fontSize: 20 }} />
            </Tooltip>
          );
        },
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (
            ![
              TransactionType.FundingWallet,
              TransactionType.Remittance,
              TransactionType.RemittanceFee,
              TransactionType.CrmAccessFee
            ].includes(data.type)
          ) {
            return null;
          }
          return (
            <>
              <span className="hover-action-content">
                <Tooltip title="Edit">
                  <IconButton
                    color="primary"
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setTransaction(data);
                      handleAdd();
                    }}
                  >
                    <EditTwoTone twoToneColor={theme.palette.primary.main} />
                  </IconButton>
                </Tooltip>
              </span>
            </>
          );
        },
        sortable: false,
        enablePivot: false,
        pivot: false,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        aggFunc: null,
        suppressColumnsToolPanel: true,
        cellClass: 'no-border'
      },
      {
        field: 'search',
        hide: true,
        aggFunc: null,
        enablePivot: false,
        pivot: false,
        lockVisible: true,
        initialHide: true,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        filter: 'agTextColumnFilter',
        filterParams: {
          newRowsAction: 'keep'
        }
      }
    ],
    [useCompactView]
  );

  const onToolPanelVisibleChanged = (event: ToolPanelVisibleChangedEvent) => {
    if (!gridApi) {
      return;
    }
  };
  const onFilterChanged = useCallback(() => {
    if (!gridApiRef.current) return;
    const filterModel = gridApiRef.current.getFilterModel();
    setFilterState(filterModel);
    setSelectedTypes(filterModel?.type?.values || []);
  }, []);

  const onGridReady = useCallback(
    ({ api }: GridReadyEvent) => {
      setGridApi(api);
      applyGridColumnState(api, keyName, {delay: 100});
      let filterModel: any = null;

      if (filterState) {
        filterModel = filterState;
      } else if (filterModel?.paymentMethodId || filterModel?.type) {
        filterModel = {};

        if (paymentMethodFilter) {
          filterModel.paymentMethodId = {
            values: [paymentMethodFilter],
            filterType: 'set'
          };
        }

        if (typeFilter) {
          filterModel.type = {
            values: [typeFilter],
            filterType: 'set'
          };
        }
      } else {
        const savedFilters = localStorage.getItem(keyName + '_FILTER_STATE');
        if (savedFilters) {
          filterModel = JSON.parse(savedFilters);
        }
      }

      if (filterModel) {
        api.setFilterModel(filterModel);
        setFilterState(filterModel);

        if (filterModel.search?.filter) {
          setSearchValue(filterModel.search.filter);
        }
        if (filterModel.type?.values?.length) {
          setSelectedTypes(filterModel.type.values);
        }
      }
      api.addEventListener('filterChanged', onFilterChanged);
      gridApiRef.current = api;
      api.updateGridOptions({
        serverSideDatasource
      });
    },
    [gridApi, filterState]
  );
  useAgGridOutsideClick(gridApiRef.current);
  const onGridPreDestroyed = useCallback(() => {
    gridApiRef.current = undefined;
  }, []);
  useEffect(() => {
    return () => {
      gridApi?.removeEventListener('filterChanged', onFilterChanged);
    };
  }, [onFilterChanged]);
  const onSearchChange = (value: string) => {
    if (!gridApi) {
      return;
    }
    const fm = gridApi.getFilterModel();
    fm['search'] = { filterType: 'text', type: 'contains', filter: value };
    gridApi.setFilterModel(fm);
    gridApi.onFilterChanged();
  };

  const debouncedSearch = useCallback(debounce(onSearchChange, 500), [gridApi]);

  const handleInputChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleTypeChange = (_: React.SyntheticEvent, newValue: TransactionType[]) => {
    setSelectedTypes(newValue);
    setIsFilterApplied(newValue.length > 0);
    if (!gridApi) return;
    const filterModel = { ...gridApi.getFilterModel() };
    if (newValue.length > 0) {
      filterModel.type = { filterType: 'set', values: newValue };
    } else {
      delete filterModel.type;
    }
    gridApi.setFilterModel(filterModel);
    gridApi.onFilterChanged();
  };
  const refreshGrid = useCallback(() => {
    if (gridApi) {
      gridApi.refreshServerSide();
    }
    setRefreshKey((prevKey) => prevKey + 1);
  }, [gridApi]);

  const sideBar = useMemo<SideBarDef>(() => {
    return {
      position: 'right',
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressFilterSearch: true
          }
        },
        {
          id: 'settings',
          labelDefault: 'Settings',
          labelKey: 'settings',
          iconKey: 'menu',
          toolPanel: McaListTools,
          toolPanelParams: {
            preferencesKey: GridType.LedgerBalanceTrasactionsListGrid,
            displayCompactViewToggle: true,
            keyName,
            refreshGrid
          }
        }
      ]
    };
  }, []);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: 'agAggregationComponent',
          statusPanelParams: {
            aggFuncs: ['sum', 'min', 'max']
          }
        }
      ]
    };
  }, []);

  const serverSideDatasource: IServerSideDatasource = {
    getRows: async (params: IServerSideGetRowsParams) => {
      if (params.api.isDestroyed()) {
        return;
      }
      const filters = params.request.filterModel || {};
      const hasPmtMethodFilter = 'paymentMethodId' in filters && filters.paymentMethodId.values?.length > 0;
      const hasTypeFilter = 'type' in filters && filters.type.values?.length > 0;
      const types = filters.type?.values?.length ? expandLedgerTypes(filters.type.values) : undefined;
      if (hasPmtMethodFilter || hasTypeFilter) {
        setIsFilterApplied(true);
      } else setIsFilterApplied(false);
      const res = await fetchData({
        variables: {
          investorId,
          limit: (params.request.endRow || 0) - (params.request.startRow || 0),
          offset: params.request.startRow,
          search: params.request.filterModel?.search?.filter,
          statuses: params.request.filterModel?.status?.values,
          types,
          paymentMethods: params.request.filterModel?.paymentMethodId?.values,
          sortArgs: params.request.sortModel?.map(
            (x) =>
              ({
                field: ['credit', 'debit'].includes(x.colId) ? 'amount' : x.colId,
                direction: x.sort
              }) as SortArgs
          )
        }
      });
      if (params.api.isDestroyed()) {
        return;
      }
      applyGridColumnState(params.api, keyName, {delay: 100});
      const rows = res.data?.investorBalanceTransactions.data || [];
      const total = res.data?.investorBalanceTransactions.total || 0;
      setCurrentTotalRows(total);
      params.success({ rowData: rows, rowCount: total });
      params.api.updateGridOptions({
        pinnedBottomRowData: [
          {
            number: 'Totals',
            credit: res.data?.investorBalanceTransactions.summary.credit,
            debit: res.data?.investorBalanceTransactions.summary.debit
          }
        ]
      });
    }
  };

  const handleExportReport = useCallback(async () => {
    if (!gridApi) {
      return;
    }
    setBackdropOpen(true);
    const fm = gridApi.getFilterModel();
    const types = fm?.type?.values?.length ? expandLedgerTypes(fm.type.values) : undefined;
    const qp = {
      statuses: fm?.status?.values,
      types,
      search: fm?.search?.filter,
      investorId: investorId
    };
    await reportApi.handleExportReport('investor-balance', qp);
    setBackdropOpen(false);
  }, [gridApi]);
  const onPageSizeChanged = useCallback((params: PaginationChangedEvent) => {
    if (!params.api.paginationGetPageSize()) {
      return;
    }
    localStorage.setItem(keyName + '_ROWS_PER_PAGE_STATE', params.api.paginationGetPageSize() + '');
  }, []);
 const saveColumnsStateOnChange = useCallback(
    (event: ColumnResizedEvent | SortChangedEvent | ColumnMovedEvent | ColumnVisibleEvent) => {
      if (
        gridApi && event.source?.toLowerCase().includes('ui') &&
        ((event.type === 'columnResized' && event.finished) ||
          (event.type === 'columnMoved' && event.finished) ||
          event.type === 'sortChanged' ||
          event.type === 'columnVisible')
      ) {
        if (event.type === 'columnVisible') {
          const colId = event.column?.getColId();
          if (colId && event.column?.isVisible()) {
            gridApi.autoSizeColumns([colId], true);
          }
        }
        const columnsState = JSON.stringify(gridApi.getColumnState());
        localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
      }
    },
    [gridApi]
  );
  const resetFilters = () => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      gridApi.onFilterChanged();
    }
    setSelectedTypes([]);
    setSearchValue('');
    setIsFilterApplied(false);
  };
  const pinnedBottomRowData = useMemo<any[]>(() => {
    return [
      {
        number: 'Totals',
        credit: 0,
        debit: 0
      }
    ];
  }, []);
  const getRowClass = (params: any) => {
    if (params.node.rowPinned === 'bottom') {
      return 'ag-pin-bottom-row-' + mode;
    }
  };

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <ScrollX>
        <Stack
          direction={'row'}
          sx={{ pr: 2, pb: 1, minWidth: 'max-content', width: '100%' }}
          justifyContent="space-between"
          alignItems="center"
        >
        <Stack direction={'row'} spacing={1} alignItems="center" sx={{ pl: 2 }}>
          <Typography variant="caption" color="secondary">
            Search
          </Typography>
          <OutlinedInput
            value={searchValue || ''}
            onChange={(e) => {
              handleInputChange(e.target.value);
            }}
            placeholder={`Search ${currentTotalRows} records...`}
            startAdornment={<SearchOutlined />}
            size="small"
            sx={{ minWidth: '180px', pt: 0.125, pb: 0.125 }}
          />
          <Typography variant="caption" color="secondary">
            Type
          </Typography>
          <Autocomplete<TransactionType, true, false, false>
            multiple
            id="transactionTypeFilter"
            options={LEDGER_TRANSACTION_TYPES}
            value={selectedTypes}
            onChange={handleTypeChange}
            getOptionLabel={(option) => formatLedgerTransactionType(option)}
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={selectedTypes.length > 0 ? '' : 'Select Type'}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  '& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
                    fontSize: '12px'
                  }
                }
              }
            }}
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                height: 32.5,
                whiteSpace: 'nowrap',
                flexWrap: 'nowrap'
              },
              '& .MuiChip-root': {
                height: 20,
                fontSize: '10px'
              },
              '& .MuiAutocomplete-input': {
                padding: '0 4px'
              },
              '& .MuiChip-deleteIcon, & .MuiAutocomplete-clearIndicator svg': {
                fontSize: 17,
                width: 17,
                height: 17
              }
            }}
          />
          {isFilterApplied && (
            <Tooltip
              title={<Chip label="Reset filters" variant="combined" color="primary" />}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    padding: 0
                  }
                }
              }}
            >
              <IconButton onClick={resetFilters}>
                <FilterOutlined />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Stack direction={'row'} spacing={1} alignItems="center">
          <CustomTooltip title="Export Transactions">
            <Button variant="contained" sx={{ fontSize: '12px', minWidth: 'auto' }} onClick={handleExportReport}>
              <ExportOutlined />
            </Button>
          </CustomTooltip>
          {canManage && (
            <CustomTooltip title="New Ledger Transaction">
              <Button variant="contained" sx={{ fontSize: '12px', minWidth: 'auto' }} onClick={handleAdd}>
                <PlusOutlined />
              </Button>
            </CustomTooltip>
          )}
        </Stack>
      </Stack>
      </ScrollX>
      <div
        className={`${mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} ${useCompactView ? 'compact-view' : 'expanded-view'
          }`}
        style={gridStyle}
      >
        <AgGridReact<InvestorBalanceTransactionDto[]>
          domLayout={'autoHeight'}
          key={refreshKey}
          getRowClass={getRowClass}
          onToolPanelVisibleChanged={onToolPanelVisibleChanged}
          onColumnResized={saveColumnsStateOnChange}
          onSortChanged={saveColumnsStateOnChange}
          onColumnMoved={saveColumnsStateOnChange}
          onColumnVisible={saveColumnsStateOnChange}
          sideBar={sideBar}
          columnDefs={columnDefs}
          statusBar={statusBar}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onGridPreDestroyed={onGridPreDestroyed}
          pagination={true}
          rowModelType={'serverSide'}
          cellSelection={true}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          paginationPageSize={rowsPerPage}
          cacheBlockSize={rowsPerPage}
          suppressColumnVirtualisation={true}
          paginationPageSizeSelector={[50, 100, 500, 1000]}
          getContextMenuItems={() => ['copy', 'copyWithHeaders']}
          pinnedBottomRowData={pinnedBottomRowData}
          onPaginationChanged={onPageSizeChanged}
        ></AgGridReact>
      </div>
      <Dialog maxWidth="sm" fullWidth onClose={handleAdd} open={add} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {add && (
          <AddCashTransaction
            investorUserId={investorId}
            entity={transaction}
            onCancel={handleAdd}
            forLastMonths={forLastMonths}
            onSave={(transaction) => gridApi?.refreshServerSide()}
          />
        )}
      </Dialog>
    </>
  );
};

export default InvestorBalanceTransactions;
